import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/1dde86ed/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageDescription = makeShortcode("PageDescription");
const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <PageDescription mdxType="PageDescription">
      <p>{`Users rely on the global header to navigate and orient themselves in your UI. This pattern outlines some of the qualities that make a global header consistent, familiar, and usable.`}</p>
    </PageDescription>
    <AnchorLinks mdxType="AnchorLinks">
  <AnchorLink mdxType="AnchorLink">Overview</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Configurations</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Navigation</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Best practices</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Related</AnchorLink>
  <AnchorLink mdxType="AnchorLink">References</AnchorLink>
  <AnchorLink mdxType="AnchorLink">Feedback</AnchorLink>
    </AnchorLinks>
    <h2>{`Overview`}</h2>
    <p>{`The global header is essential to a product’s UI. It is a consistently available user interface element that contains functionality for the current product as well as for the entire system. Like the macOS Menu Bar and the Windows Start Bar, the global header provides consistent locations to invoke your product’s local navigation as well as pervasive access to system-wide functions like system settings, notifications, and navigating between products.`}</p>
    <p>{`This pattern covers the foundation of using UI Shell components for within and between product navigation, and introduces techniques for achieving consistency in products.`}</p>
    <h3>{`Anatomy of a global header`}</h3>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "27.142857142857142%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAFCAYAAABFA8wzAAAACXBIWXMAAAsSAAALEgHS3X78AAAAt0lEQVQY02NgAIL/6l0iv1VaJP8z/GcA83X6xcG07AKu/xpzeRlwAJD6/1r9oiD2L91OSZg4k5ycXIeGuvoaFRWVpRISEsukpaVBeLmMrOwyWVnZ5WJiYhgYqG65vLw8SH6JooLiWqDaDpBZDNu3bmPZu3fvo2vXrv0/c+bM/zOnT0NoID579iwYnwaKYcMwuStXrvwHmbFjyzYWhv///zMCsS0Q+wGxFxB7k4hBenyhZjAyUBsAAC8Do5tF3zb6AAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example diagram of the global header.",
        "title": "Example diagram of the global header.",
        "src": "/static/747f762a7c63f0bf743bc3c32a5bcd8a/3cbba/header-anatomy.png",
        "srcSet": ["/static/747f762a7c63f0bf743bc3c32a5bcd8a/7fc1e/header-anatomy.png 288w", "/static/747f762a7c63f0bf743bc3c32a5bcd8a/a5df1/header-anatomy.png 576w", "/static/747f762a7c63f0bf743bc3c32a5bcd8a/3cbba/header-anatomy.png 1152w", "/static/747f762a7c63f0bf743bc3c32a5bcd8a/0b124/header-anatomy.png 1728w", "/static/747f762a7c63f0bf743bc3c32a5bcd8a/99571/header-anatomy.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Hamburger:`}</strong>{` The hamburger icon is used to open product navigation such as the `}<a parentName="li" {...{
          "href": "/components/UI-shell-left-panel/usage/"
        }}>{`left panel`}</a>{`.`}</li>
      <li parentName="ol"><strong parentName="li">{`Header name:`}</strong>{` For IBM products, the header name is always preceded by “IBM.” This should always and only link the user to the domain’s home page.`}</li>
      <li parentName="ol"><strong parentName="li">{`Header links:`}</strong>{` Links in the header are supported as product navigation, if required. These links should not open a new tab or link to another domain. These links drop down to the side menu in narrow screen widths.`}</li>
      <li parentName="ol"><strong parentName="li">{`Sub-menu:`}</strong>{` Sub-menus are supported as product navigation, if required. Dropdowns open on click and are closed by either selecting an item in the menu, clicking outside the menu area, or clicking on the menu label. When open, the chevron should point up. Dropdown menu labels serve only to open the dropdown; they cannot be a link to another page in the product.`}</li>
      <li parentName="ol"><strong parentName="li">{`Utilities:`}</strong>{` The header provides a home for global system-level utilities from anywhere in the site. Utility icons should not be used to directly navigate to an area. Instead, they should open a panel that provides access to other places in the product.`}</li>
      <li parentName="ol"><strong parentName="li">{`Switcher:`}</strong>{` The switcher provides a way for the user to navigate easily between products and systems. Recommended uses for this component include recently used apps, frequently used apps, or all apps attached to the user’s account. If the list is a manageable size, include all apps or products available on the system.`}</li>
    </ol>
    <h3>{`Header and panel persistence`}</h3>
    <p>{`Global and local refer to the location and persistence of header elements in your UI.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Persistence`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Definition`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Global`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A global UI element is present everywhere in your UI. It contains system and product items your user may need at any time: navigation, authentication, notifications, and more. Global elements remain consistent from one context to another.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Local`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A local navigation exists within a product’s context and provides the means to accomplish product level tasks. Local elements will therefore differ from one product to another.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Task hierarchy`}</h3>
    <p>{`The task hierarchy describes the breadth of effect for various tasks. For this guidance we use the term “product” to encompass the broad category of products, applications, offerings, or web properties.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Task`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Definition`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`System`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`System-level tasks include navigating to the main sections of a platform and accessing system utilities like notifications or settings. At this level a user can manage the attributes that apply to the whole system.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Product`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Product-level tasks include navigating within a product and accessing the product’s core functions. At this level the user directly interacts with the product’s main function.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Examples`}</h3>
    <p>{`This matrix shows common system and product level tasks that appear globally and locally.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} mdxType="Column">
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}></th>
              <th parentName="tr" {...{
                "align": null
              }}>{`System tasks`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Product tasks`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><strong parentName="td">{`Global persistence`}</strong></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Log in, notifications, profile access, dashboard link, setting time, billing information, user permissions, switching from one product to another.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Hamburger menu to toggle local navigation. Show/hide application navigation, document thumbnails. Consistent placement of local actions in common between products for example: new file, save, cancel.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><strong parentName="td">{`Local persistence`}</strong></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Detailed system-wide settings, such as user rights management, notification preferences.`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Commenting in a text document, navigating from one peer resource to another (for example from one container, data base, or document to another), selecting modalities within your product.`}</td>
            </tr>
          </tbody>
        </table>
      </Column>
    </Row>
    <h2>{`Configurations`}</h2>
    <p>{`The UI shell is configurable to let a product or platform choose which shell components and configurations match their user and information requirements. Simple products have the flexibility of using a header or side panel and more complex products require a combination of UI shell components to accommodate the depth of their navigation.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "33.125%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAABQ0lEQVQoz2NgoDb4//8/47f/f5jfA+kf//8zQYUFgZinWd5PwYtBRgLI5gRiXpCEsI48OzMbqxiQyQfE3CB1UDl2sM6FSxaz79i4hf/E9v0ix7fsFQKJLV++fP2a1at/1BVVvmkoq367aePGN8tXrPhy5vBxyyBv/9jIiIj/M2bMeLVy5co3q1ategnEf9auXTsB7spNrMGcG/J7FHeENckCuYxAV+8E4v8f37z7/eXDp39AJgj////3v2+cpXdRZU7R/79//vyGiv/+DwFz4AZesqhl2aiQyHlQNRvkNYbfv3+f/I8d2AgICqQmxMdjSPz8+XM52LCVqgkY4Qo0MPbv37+tQLr2z+/f9UB23a9fv5qB+mRFRET0QsPCWn/8+FH958+fGqB4FVBdC5D2gRtwSi2fYaNoNMM2iXhQJFESwQwAHTfcBovfrqwAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example the header and left nav being used for the same IA.",
        "title": "Example the header and left nav being used for the same IA.",
        "src": "/static/0eba05fd4412bd1d8eaa79d532dce088/3cbba/configurations.png",
        "srcSet": ["/static/0eba05fd4412bd1d8eaa79d532dce088/7fc1e/configurations.png 288w", "/static/0eba05fd4412bd1d8eaa79d532dce088/a5df1/configurations.png 576w", "/static/0eba05fd4412bd1d8eaa79d532dce088/3cbba/configurations.png 1152w", "/static/0eba05fd4412bd1d8eaa79d532dce088/0b124/configurations.png 1728w", "/static/0eba05fd4412bd1d8eaa79d532dce088/99571/configurations.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">
  Example showing how a simple architecture can be configured with only a header
  or only a left panel
    </Caption>
    <h3>{`Header only`}</h3>
    <p>{`The UI Shell header can be used as standalone navigation for your UI when a small number of main sections do not require a secondary navigation. The header provides a place for a site title, navigation links and dropdowns, and header icons. The header is globally persistent and will always be in view as the user navigates around the site.`}</p>
    <p>{`This configuration gives more horizontal space for the page’s content, but has limited room for navigation items in the header. This configuration also does not lend itself to sub-menus that need to remain open as they will overlay and interfere with the page content. Use a left panel in combination with the header if your navigation requires a sub-menu to remain open.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.09239130434783%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAAB0UlEQVQoz51TS0sCYRSd/Iz6MUKTiwojh8YsScXwRRBUuoikaN2iWrRoVW2qaRlRyxa6kDQtogeDQbsCf0CLfCx9zozzde80mmYEdeDOPd/MPWfOHRiGAYTDYWxMKBQyLEciZGrCTpbcs2TVNw+1QCIzc2TNv0jC0wHCj/PEZrMRnueJ3W4nwWCwB7Uej4fpgMvl6mH+CZPJ1Kl1u90G7E6n0+sP+E8twyOH3rFJwcc5BK91SvCMTgg+q0OYHuIEdmBAYAdZgWXZI7PZfMJxnAO1FovF0DKMRqO92GOx2HEmk6HnZ2f08iJGH65u6PO9SJ9uH7UuXt/RVCpF0+k0TSaTVBRFmkgkdlAbj8eNLcNqtaodarXaLgWUy+VKg6oS0N+qSj+xgVropGVYr9eNuvG+PiRRVVUbjQZcVCrLsipLMtz55JIk4TMZB0ul0pau7TaEhJohCCVVB57RWFGU71wzBCPNEF5CulauVCp7OASCGgwokEZBIMf6xuv659nUw/yY8ID+ERBmuyth0x0ersNK77DpC6TLAs+CJgtptGrnMPMK9QZbreihvgzz+bzWc7mcsVgs9kP1FQqFPuxN3jy3c5wFDWn3aAGG/vujdGg/AOQz7uIo4nrOAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of a header as the global navigation",
            "title": "Example of a header as the global navigation",
            "src": "/static/2bfb0aa1a103c9d7f308582597bad37d/3cbba/header-only-example.png",
            "srcSet": ["/static/2bfb0aa1a103c9d7f308582597bad37d/7fc1e/header-only-example.png 288w", "/static/2bfb0aa1a103c9d7f308582597bad37d/a5df1/header-only-example.png 576w", "/static/2bfb0aa1a103c9d7f308582597bad37d/3cbba/header-only-example.png 1152w", "/static/2bfb0aa1a103c9d7f308582597bad37d/392b1/header-only-example.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Example of header-only navigation</Caption>
      </Column>
    </Row>
    <h3>{`Header with left panel`}</h3>
    <p>{`The left panel allows for more navigational items to stack vertically and an additional level of hierarchy when paired with header navigation. Compared to a header-only site navigation, this arrangement means sub-menus can remain open without overlaying or interfering with the page content.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.09239130434783%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAACH0lEQVQoz51Tz28SURDest2/RE+GbgARsuxCsRtq2diWYlMuJiYmJqaJSk9mKRrwUDl4U9F48cfFGhOjBxLKwoUTJ5YgEMKBgwcv9OCFX1tgnPcKazA1MZ3ky8xk3/fNzL55jH5xj1GCQYbY5qpi2dkMs/ZLS6zoFthAIMDKsvxPkO+RSGSBcEOhENVgKhei1IdlZYE5p1mt1nnulrJuoR0q18PbG6H34hXhhdt5OS24nWmXy5V22O1pm82WtqOfAfOXDofjrd/vXyNcj8djOe0QRxa21xZJHLm28W5v9x743QLE1RR8+KzBp8OPcHSUg0KhAPl83kQul4NSqQTZbPaAcDOZzOJ0ZBTcv8WS+O7N26/V+D4Ioqf/9fCLcXz8y+h0OgYAnIU+nFqccNGzZoerT6M0eXDn/puDVAqW/csn2rfMBA9NxuOxieFwSL1hGJMTNKLW7XYfE+5gMJgJRhle8dLkxtXgq531LRBsTmMqCEgGFKGYxahlCvb7fSqIxVjzUiSPSOeXRPGZV5KAX+IHmqaN8PwIK49QaHSGDaeCj+YEdVwbUZKooNfrfb4irwDP81AsFuF/DAs+IVxs+M/IKEQTn8/3EOOfuBJVvMUGnm9gBw2s3kDC3/iOnf/o9Xq7c/+QWCKRoD6ZTLIYc7FYjMM14Wq1GletVrl6vc6Vy2VO1/U5VCoVrt1u0/1rNpvz266q6nkfCtNqtcz4N0QRrYjdWyUqAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of a left panel.",
            "title": "Example of a left panel.",
            "src": "/static/1b050c3c6a7c5bdd2b05c4e8ed57f925/3cbba/header-leftpanel-example.png",
            "srcSet": ["/static/1b050c3c6a7c5bdd2b05c4e8ed57f925/7fc1e/header-leftpanel-example.png 288w", "/static/1b050c3c6a7c5bdd2b05c4e8ed57f925/a5df1/header-leftpanel-example.png 576w", "/static/1b050c3c6a7c5bdd2b05c4e8ed57f925/3cbba/header-leftpanel-example.png 1152w", "/static/1b050c3c6a7c5bdd2b05c4e8ed57f925/392b1/header-leftpanel-example.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">An example of a left-panel-only navigation.</Caption>
      </Column>
    </Row>
    <h2>{`Navigation`}</h2>
    <h3>{`Global`}</h3>
    <p>{`A global navigation is always present in the UI. In this example, the `}<a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/UI-shell-header/code"
      }}>{`UI shell header`}</a>{` is used as a global navigation with two system level links (A) in the header and five system level links in the switcher.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "41.25%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsSAAALEgHS3X78AAABy0lEQVQoz2M46t/CAAJHfJr4zmRMlTpePVdk/7UzXIf3H+Tdt28f7////5ns7e3YAwICOiwsLCe6unv3eXl59bt7uPfF+4ZNKPNNnBbq4jPLx9/XD2zQBvsiML1WP519f8McycNTVwkt37SWpzy/iAUozAiSS05JE4yIiPifkpr2/9S5q//v33/w//Xr1/9/fv/xf/eOnf+DA4L+m5iazgAbZLyuHEzzbMtimLhoNu+FG1cEly9dJphbkMe099BJsFxUXLIA0LC3ubm5/379/PET6OpfMHz33t1vkyZN+l9XV9cNVrxxzlIwPadtAuOebTu5Tx4/IXDgwAHewvwCBgsLc7BccXGRgI2NzYdwoCt//vz559+/f39///4NxkDw4z8E9IIVV1VVgen6+nrG6upq1v3793PMnDmTw8HBgQFoCFguKytL0MjI6HdMTMx/XABo0WQGGNixYwec3dLSwlpbWwtmx8XFgen8/HxWS0vL0Ojo6KivX79GAjVH/vjxA4YjgDjh27dvRmDFjx8/xmow0FIw7evry0As+PXrFwODmJgYmAMMcAzDgC4C03l5eQzm5ubMQD4z0CXMQBcyA10FpmH4+/fv4BQBAO0MDeSYrJ66AAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Diagram and image of the header nav being used as a global navigation.",
        "title": "Diagram and image of the header nav being used as a global navigation.",
        "src": "/static/db44ec2ec01225d458da5bdac92b7090/3cbba/global.png",
        "srcSet": ["/static/db44ec2ec01225d458da5bdac92b7090/7fc1e/global.png 288w", "/static/db44ec2ec01225d458da5bdac92b7090/a5df1/global.png 576w", "/static/db44ec2ec01225d458da5bdac92b7090/3cbba/global.png 1152w", "/static/db44ec2ec01225d458da5bdac92b7090/0b124/global.png 1728w", "/static/db44ec2ec01225d458da5bdac92b7090/99571/global.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">
  The global header in this example contains only system navigation elements.
    </Caption>
    <p>{`In the example below, the `}<a parentName="p" {...{
        "href": "https://www.carbondesignsystem.com/components/UI-shell-header/code"
      }}>{`UI shell header`}</a>{` contains both system (A) and product (B) links in the global region. System level links should be right justified in the header and product level links should be left justified. On narrow screens when there is no room for header links, the system (A) links will move to the right UI shell panel and the product (B) links will move to the left UI shell panel.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "41.25%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsSAAALEgHS3X78AAABdklEQVQoz2N4ysDPcDKkQ+h02Ryx0+VzRU/WLxA7njNV/KR+CQcDFMQkxLOnJCe3JYRG9RVFp0/NCk+Y4O7m3uPh4dFrZ2fX5WftsjjaLyQQpp5hn3mJ8OHi6Qr7g5slVzK4sK9STuA8pJnL8v//f7D8g4cP+a5du/b/8d0H/189ef7/0+t3/1+/ev3/24/v/7et2fg/yNv/v72++Vy4gesMM7mWeRcLrbLJ42dAAl8fv2YE0T9+/OAFGv4WiP8B8S9kfP/G7W8zJk7531JW2wfWJHGwlOE/wzYg9mD8z+DKUJyew7BHJYPhsHouw6//f8FqPn36xPvr16/3//79+/8bAv4A+X9A9J/ff378h4AusOL/DIuRHQXkL4Szf/78CaY/fvzI9weo9T8eAPTFRLjGCyqFcHxeuQAuBnQFzEDWv3//RgL1JQDpOCAdh0THAl2eBlRrzEAM+Pz5MwOxAGgwI14FSF5mAHqZGaiBGRcNdCETSC0AE3ge5lSEIAsAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Diagram and image of the header nav being used as a global navigation.",
        "title": "Diagram and image of the header nav being used as a global navigation.",
        "src": "/static/9b225724b1c8ac9dd511b6f7faf6cd20/3cbba/global_02.png",
        "srcSet": ["/static/9b225724b1c8ac9dd511b6f7faf6cd20/7fc1e/global_02.png 288w", "/static/9b225724b1c8ac9dd511b6f7faf6cd20/a5df1/global_02.png 576w", "/static/9b225724b1c8ac9dd511b6f7faf6cd20/3cbba/global_02.png 1152w", "/static/9b225724b1c8ac9dd511b6f7faf6cd20/0b124/global_02.png 1728w", "/static/9b225724b1c8ac9dd511b6f7faf6cd20/99571/global_02.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">
  The global header in this example contains both product (B) and system
  navigation (A) elements.
    </Caption>
    <h3>{`Local`}</h3>
    <p>{`The local navigation takes users between areas of a product. Generally, these areas are collections of pages that should be grouped together so a user can undertake an end-to-end workflow without changing areas.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "41.785714285714285%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsSAAALEgHS3X78AAABwklEQVQoz2NgAIJtu3eyntp7mOfYrv3cJ3Yd5D518Cj3rqMHuCbk1TOC5Bn8ZFiC/QI6rM0tJ3p4+fX7+Pj2e3p69oeHh/dnZmb2xsXFzUpJSfEDq/3PsJ/hWuUS/rPztojdmLtD+OL0TULnulYJHwpo5WCAgi1aBTqxYVH/M2KS/586c+X/gwcP/r979+7/hw8fwDQIPHz4cCrEwKQLDDdip/Edqpwl9HjdMelLUzaKHUuaIPA5dhE7zMAFiikKaanp73JTM//++vb9J1D/LyT87T8EdIIVM/+fB6aPzljLeWzrXtF9izfw/edoBnu1wDoUTBd5xvA7W9m9jwkK///zx89f//79+w0Cf/78AeHvINN+/foFMXB57QQwHVWRxbxu+2aurpmT2GAuM7Q2A9O+oYF8BoYG/2Pj4v7jAkADJ8P0MWydswJMNzc0smbEJ4NdtXP2SgYrcwuweHhgCKuJsXFqeFh4ztevXzN//vyZ+ePHDzANxBlAduH379+twIofPXoECcv//+EWILMtLS0ZiAVAwxkZxMTEcCqwsIC4MDg4mMHIyIgZmExYvn37xgLUyAJ0FZgGYmYQDXQhE0gtAOMlEeNBhZhKAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Example of left nav navigating within an offering.",
        "title": "Example of left nav navigating within an offering.",
        "src": "/static/b4e24ece12ae7d03d01507dd688004bf/3cbba/within-offering.png",
        "srcSet": ["/static/b4e24ece12ae7d03d01507dd688004bf/7fc1e/within-offering.png 288w", "/static/b4e24ece12ae7d03d01507dd688004bf/a5df1/within-offering.png 576w", "/static/b4e24ece12ae7d03d01507dd688004bf/3cbba/within-offering.png 1152w", "/static/b4e24ece12ae7d03d01507dd688004bf/0b124/within-offering.png 1728w", "/static/b4e24ece12ae7d03d01507dd688004bf/99571/within-offering.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">The UI shell left panel being used as the product navigation.</Caption>
    <p>{`Related products should share navigation structures. Following similar structures across a platform minimizes transitional volatility—your users will spend less time orienting themselves. This will help increase the user’s productivity and perception of the platform’s responsiveness as they move between screens and states.`}</p>
    <h2>{`Best practices`}</h2>
    <h3>{`Persistent data and UI state`}</h3>
    <p>{`The UI shell makes it easy to pivot between different offerings in your UI. Maintaining or restoring the state of a page helps your user pivot between different areas to complete tasks without losing context or progress. If state or progress will be lost, inform users of this consequence. Maintaining these states and filters will bring the user back to where they were if they had gone through levels of drill downs or welcome screens.`}</p>
    <p>{`One technique to maintain this state is to use the shell’s menu items to track the essential state elements in the URL and return the user to that URL automatically when they return. This capability is not part of the UI Shell component and must be added during implementation.`}</p>
    <h3>{`Sense of place`}</h3>
    <p>{`The role of the global header goes beyond linking the user to different areas in the UI. The global qualities make the header an ideal location for your user to naturally reference when they need to orient themselves.`}</p>
    <p>{`This sense of place applies to location as well as states. The header can be used to indicate the user’s logged in status, which account they are using, or if they have entered a different mode.`}</p>
    <h3>{`Drill down levels and breadcrumbs`}</h3>
    <p><a parentName="p" {...{
        "href": "/components/breadcrumb/code"
      }}>{`Breadcrumbs`}</a>{` let your user see where they are in the hierarchy of the application and gives users a way to navigate back up.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "41.785714285714285%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsSAAALEgHS3X78AAAB1UlEQVQoz2NgAIIdO3awHjt6jPvI/oPchw8d5jp27Bj3vv37uZpKqhlB8swBGqz+Af6t1hZWkyOt/GZHOPrOcPfy7AsPD5+QmZnZGxcXNyslJSWQAQbmz5/PtnPXLoGt5w9zbd+5g3vdunXcpkYmTAzBimD5ZpM4UaDmf8mJyf+P7zz5/97FG//fvXv3//2HD//fv3//HwQePnw4C25gYGQo8+zJ07nvLz8ou2vtZt7Y9CSWrrQKsOv+s/5n3CybrVGQlPWiNDXn/89fP34C9f9Cwt/+Q0Av2LDLmw+B6cMrt3LcnbBZbdui1Twg/r9nHxgSbHzBhqpU+HDY2ti8Cg0N/f/j+49f//7++w0Cf/78AeHvINN+/frVC3chwxIfhhmFzaz3GtZIze+awgESur36IIOehTFYOto/lNfQ0PBXVHTUf1wAaP50sOI/0Uvh5j5JmS++L6GbCcS+EDGBwdLSEiweEhzCamJikhwSEpLz5cuXzJ8/f2b++PEDTANxBpBd+P37d2uw4v+hKyD0//8MOzWzGZvkfMD8ZZbZYBpmKDEA6G1GuGHIoIBBHUxbWFiA6eDgYAZjY2NmoAtZvn79ygLUyAJ0FQvQdSDMDKKBLgT7DADaPBRUe0UxeAAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Breadcrumbs being used to navigate up a level.",
        "title": "Breadcrumbs being used to navigate up a level.",
        "src": "/static/0d33293c27ef2c438b2bad8dacd4d3c3/3cbba/drill-down.png",
        "srcSet": ["/static/0d33293c27ef2c438b2bad8dacd4d3c3/7fc1e/drill-down.png 288w", "/static/0d33293c27ef2c438b2bad8dacd4d3c3/a5df1/drill-down.png 576w", "/static/0d33293c27ef2c438b2bad8dacd4d3c3/3cbba/drill-down.png 1152w", "/static/0d33293c27ef2c438b2bad8dacd4d3c3/0b124/drill-down.png 1728w", "/static/0d33293c27ef2c438b2bad8dacd4d3c3/99571/drill-down.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Breadcrumbs being used to navigate up a level</Caption>
    <p>{`In many cases users need to drill up into a new context, for example from an overview page to a particular project, device or asset view.`}</p>
    <p>{`A drill down can be triggered from any interactive element in an application, and will generally open a new page focused purely on the object that was selected. This new page will then include a breadcrumb of the path back to the root level above the title.`}</p>
    <h3>{`Organizational schemes`}</h3>
    <p>{`When planning your product’s UI, put the emphasis on the tasks the user has to complete rather than business or technical limitations. Forcing the user to learn a new mental model for your product increases the time to productivity and creates a disjointed experience between your product and the platform.`}</p>
    <p>{`Your UI may need different schemes for organizing content in different parts of your UI. Create logical groups that align to your user’s goals and improves access to the content.`}</p>
    <h4>{`Most recent`}</h4>
    <p>{`Ordering a navigation by most recent helps users who are frequently looking for the last object they used and the historical context helps with discovery. This loses any logical grouping and may be suited as an alternative way to organize rather than the primary way.`}</p>
    <h4>{`Customized`}</h4>
    <p>{`A user customized navigation lets the user personalize the UI for their own needs.`}</p>
    <h4>{`Audience`}</h4>
    <p>{`Structuring content by your audience takes into account the role or permissions tied to that persona. This can highlight more common tasks related to that persona, but can also decrease dicoverability if tasks overlap personas or your user occasionally shifts between roles.`}</p>
    <h4>{`Alphabetically`}</h4>
    <p>{`Organizing your navigation alphabetically is only successful if your user knows what they are looking for and how the item is labeled. Alphabetical navigation decreases discoverability in cases where your user is looking for a synonym of an item (e.g. “pop-up, modal, lightbox, dialogue”).`}</p>
    <h4>{`Company organization`}</h4>
    <p>{`Your navigation may be composed of multiple applications, resources, and platforms working together. The navigation should reflect an appropriate domain model rather than an org chart or a series of company or technology acquisitions.`}</p>
    <h4>{`Unbound content`}</h4>
    <p>{`Avoid placing unbounded content in the shell side navigation. Usability drops when the number of items within the shell get too high. For this reason, do not place content that has no upper limit (such as content created by users) within the shell’s side navigation. Instead, make use of drill down patterns.`}</p>
    <h2>{`Accessibility`}</h2>
    <h3>{`Matching source code order to the visual hierarchy`}</h3>
    <p><a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Techniques/css/C27"
      }}>{`Technique C27`}</a>{` outlined in WCAG 2.1 recommends matching the visual order of your UI objects with the order they appear in the Document Object Model (DOM). This technique ensures the designed hierarchy of information is communicated the same visually as it is by assistive technologies.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} colMd={12} colSm={4} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "46.51785714285714%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsSAAALEgHS3X78AAACLElEQVQoz32PS08TURiGjwIL1JgUFy61wMKF/0XdyU1DhAaUYHRv4sKVYohuXLEyUaIohFgXJl5C0mgqtqVCqm3pbSgz05nOmTNn7tP2dRqjYaE8yZPvO4v3zXfI2uvVnrq4P+R3WoOUsyizeNRrB0Mq1U6PT149M3f75vDM/I3o9Vvzg7G52ei12djwZGzq1OWRkejYlYnzoxPj56ZmYkPT09Nnx0fHjhIv8CO1SlWtlit+qVC0i/mCqUgyTG4uJxOf32eSm0gnN63Ul6/O91SGl34U8HMnt5BOpT9lUmlspTN2OINsNqsUd4sRQvNCpLknmS63YWoMhko7aAGWpK3WdgobnDKoooxGXQRTtTZCrAZ9xGVtgwoShHwpoFURjmUzq+1FiLJTjmj1BuOaAd402iY1AwSAKdNX5Vzhoyzso1oo+cJupVUrlj3e1NGUlUWm6ynfciDW6rAZh6ZpHcd1B4hhWhFOFd3UJASm2oJHfcBDx+MrlZqQYIYBUZIgyRL26nvdA8FN/pjq9J6iqh8aSmNdZ/pbxthLy7JOEIANvNl0/KcJ4Fno80QLa1vAxra97lp0hYZJbhiCYRj10GqozTm/Q/4HoJ+8+8JNXlrwcxcf+NsX7vvZ0MrDdWcR2D/yrYzjjuMcs22733Xd/u4elvZ1s/F4nCwtLZFcLkckSQq7QMiTd61wyfcCmT5gq2vv71nqIYcQlv7TQwEoERVOwi/+9WDwz/sgvwAwCuJ9MpM3nAAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "hint text",
            "title": "hint text",
            "src": "/static/6d853354b5661ce56a757463fef585a7/3cbba/matching-dom-visual.png",
            "srcSet": ["/static/6d853354b5661ce56a757463fef585a7/7fc1e/matching-dom-visual.png 288w", "/static/6d853354b5661ce56a757463fef585a7/a5df1/matching-dom-visual.png 576w", "/static/6d853354b5661ce56a757463fef585a7/3cbba/matching-dom-visual.png 1152w", "/static/6d853354b5661ce56a757463fef585a7/0b124/matching-dom-visual.png 1728w", "/static/6d853354b5661ce56a757463fef585a7/99571/matching-dom-visual.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
Example showing a text input component as it appears visually in the UI, HTML, and accessibility tree
        </Caption>
      </Column>
    </Row>
    <p>{`This technique is necessary when the organization of your navigation menu is used to convey the meaning of stepped concepts like provisioning or other wizard-style patterns. The intended meaning of your grouping and ordering may be lost if the position of an object is styled differently from the DOM order.`}</p>
    <p>{`In some cases, styling the elements with CSS to appear in a different visual order than the DOM order may be beneficial. For example, on narrow or mobile screen widths you may choose to move your navigation from the top of the screen to another area that’s more appropriate for your user’s context.`}</p>
    <Row mdxType="Row">
      <Column colLg={12} colMd={12} colSm={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "38.92857142857143%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAICAYAAAD5nd/tAAAACXBIWXMAAAsSAAALEgHS3X78AAABmElEQVQoz4WRy0sCQRzHV/c/KOgSXYSe1xJ8UJrQ9jJfECHVlr0gKKhOneqQKxWB6P/SQapDpw6VZilBUGnrbGRRJ9c2H79+O5ZEJH3hywzMZz4zO8ssr61omB8BBrRMjUzxfJWNucOaWhzD+6a5uYnp4Izbu7XkmAgujk0FnNyQ0G3pEaxWq99ms217PJ4WlR0ddGobIUL3OYbtPuye2WQWOI7b5Hm+ni6cnp+FH0UC17ErIKkHeEoTkMQMiBmsKEI2m4VkMulS2UNyyd6M7LBZZqVhdX7pYtY7CccHR0AkArFotL3ymQACVCJjP35Uwb6rC6VSaVBlpbdnVrAvaIB51boG7JE+Sy9cJ5Iq+/ry/NJMhYqiUGGxWJSxH2oKhQItRkEZ5HI5Kry/vWOb9B30/Tr1XftGoxGuEgkqJIRUhSH4J/l83qmyqVSKNZtMVIiyE3xjQBFlJElq+xb2l8vlEN7Oj2MAxwDeivZrvivLcqvKptNpjcFgoPtwHEepPx6Pb6BvHYV1DApq//5fwYOrc51O9yfzCWP6Mu8bCkq/AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "hint text",
            "title": "hint text",
            "src": "/static/fc4d4389dbd679f49034d7e754740a25/3cbba/nav_priority_changes.png",
            "srcSet": ["/static/fc4d4389dbd679f49034d7e754740a25/7fc1e/nav_priority_changes.png 288w", "/static/fc4d4389dbd679f49034d7e754740a25/a5df1/nav_priority_changes.png 576w", "/static/fc4d4389dbd679f49034d7e754740a25/3cbba/nav_priority_changes.png 1152w", "/static/fc4d4389dbd679f49034d7e754740a25/0b124/nav_priority_changes.png 1728w", "/static/fc4d4389dbd679f49034d7e754740a25/99571/nav_priority_changes.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  1. Navigation items appear in the header at wide widths. <br />
  2. Navigation items have moved to a side nav for narrow widths.
        </Caption>
      </Column>
    </Row>
    <h4>{`Navigating content by headers`}</h4>
    <p>{`Users who rely on screen reader technology to navigate areas of a site also rely on screen readers to navigate the content of a page. In the same way users can visually scan for the larger and bolder type of a header, users of assistive technologies also must understand the hierarchy of the page’s content to efficiently navigate a page’s content.`}</p>
    <p>{`To ensure all users interpret the structure of your content in the same way, the visual representation of a heading should match the underlying ranking of the header tag. Tutorials on how to achieve this can be found in the WCAG page structure `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/tutorials/page-structure/headings/"
      }}>{`tutorials on headings`}</a>{`.`}</p>
    <h3>{`Keyboard navigation`}</h3>
    <h4>{`Skip to main content`}</h4>
    <p>{`Some users may use a keyboard to navigate your site. Starting focus in the main navigation lets them quickly navigate to other areas in your UI, but could block them from the main content if there is a large number of navigation items to tab through first.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.w3.org/TR/2016/NOTE-WCAG20-TECHS-20161007/G1"
      }}>{`Success Criterion 2.4.1 (Bypass Blocks)`}</a>{` suggest bypassing these blocks by providing “Skip to main” link at the start of you navigation’s focusable controls. This lets users easily skip the navigation region and begin interacting with the page’s main content area.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} colMd={8} colSm={6} mdxType="Column">
        <img {...{
          "src": "/5d3277cc05d3ea02ae87c04f103a4bae/skip-to-main-content.gif",
          "alt": "hint text"
        }}></img>
        <Caption mdxType="Caption">
  The "Skip to main content link" is the first focusable element on the Carbon
  website.
        </Caption>
      </Column>
    </Row>
    <h4>{`Landmark regions`}</h4>
    <p>{`Landmark regions are a way of grouping similar areas of content in your UI and assigning them roles. This technique lets users navigating with assistive technologies quickly get around your site by navigating between landmark regions in your UI rather than each individual element.`}</p>
    <p>{`Examples of common landmark regions include: navigation, main, form, banner, and search. If there are multiple navigation landmark regions give each a unique label.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} colMd={8} colSm={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "60.32608695652174%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAACe0lEQVQoz2NgAIL///8zUALuKjcw/meIh3BghoHoyRsWs6a2VbDd+vyU7e7f/2zX//9ne/7/G9tPIP0bCf8CYqB6tun/b7IJ///PCDP4IUMyA4O5iRmMzxIeHDopxD9wh5627gZ9ba1NBlpam/Q0dTZpq2ls0lJVR8IamzRV1Te6apvvTtB1C/wvMUljEXMQK9gUXx0rMM3DwMAa4x20KTww5KmjgcmJxTM271+74dCDXes33Txz+MTt88dO3T539CQM3wLiG1cPn355Zf2BeKAfma73bWQGG+Rl7QhzIXtBes7VrLyc//bmVm/unr9599mbt/9fv3n9Hxv4B6W/PHyZD9L84/t35ocMaQwMTeZxsDBgbiivOV9ZU/3fydL22Yf7z25A9QCD7f9fEPj969dfoEl/f//+/ffPnz+/QJKf7j7LBmn++f8P8wOGRIhJb4UbZG6IlBp5u7if9fTx+m9jZP783d0nN0Ea/vz+AzLrP9CA/79+/QKZDKL/AQ39A5L/ePpu+QOGcI3XKcvZIaYBXfmfYS7jGe1yLntTq3Nmlhb/bY0tnr698wTswp8/fwId9gvkIpjLQPQfmAs/3nqSCzLmy7kHkDBMN/QGe3m+ejSro4XNdWsHu//Wxubv3t9//uA/EeDz/RcFYC9//QbxcriJC9jgDRqprO7mdpttLK1eOBlbnnhz9f6ePz9+Pfr+4fOtH5++3vn15dudn58RGMi/+fPT1+cfrj6MA7vw0StmmEFg+rhqLsNqzWSuFZpJvECa+7x3G9cZ3zae8/4dPOeA+IxPKw+ID8c+bTxnfdt4L/t1gdPfBb8ORNY5qJZNUda7FNoLZwMAQzzL7shVdWsAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "hint text",
            "title": "hint text",
            "src": "/static/b8294005a25e59d81ef5e1c4c0657bf8/3cbba/landmark_zones.png",
            "srcSet": ["/static/b8294005a25e59d81ef5e1c4c0657bf8/7fc1e/landmark_zones.png 288w", "/static/b8294005a25e59d81ef5e1c4c0657bf8/a5df1/landmark_zones.png 576w", "/static/b8294005a25e59d81ef5e1c4c0657bf8/3cbba/landmark_zones.png 1152w", "/static/b8294005a25e59d81ef5e1c4c0657bf8/392b1/landmark_zones.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">
  1. Navigation landmark <br />
  2. Search landmark <br />
  3. Main landmark <br />
        </Caption>
      </Column>
    </Row>
    <h4>{`Navigating the page via regions`}</h4>
    <p>{`Navigating between landmark regions helps users who cannot see the visual grouping of your navigation. This grouping can be important to understanding the organization of the structure of the content and making it clear what users can do and where they can go in your UI.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} colMd={8} colSm={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "69.70108695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAYAAAAvxDzwAAAACXBIWXMAAAsSAAALEgHS3X78AAACsElEQVQ4y62SXU/aYBzFsTpg8a1FBwwQEJCXQtAw5F1eVOp0ihIUJjim4qy6aVzwgiXL4s0+xK4kWbJ9EMPljAmXS/Y9tE9y9kA2Xa/nPzlp/037yznnqQKA4qFGxuos/yvZCNnsF0EQruLx+GU0EmnFYtFWPBZrzcwkW6lUSqZkMkmVaqXT6ctEIvmD6ms4HFbKgMFg8CoQCMDr9cJlscNltsJttWOj8BKNDw3U63WcnZ2h0Wjg8PAYtZpI9zpWchVE40s/A4GEWgYMhUKXPp8PTqfzVjesIyPqUaJRc2RJeEFEcZ/s1WrkQBTJ9naVlDfL5O3RO3J+/uk2t1pFbCZ/nc3m5cAIjUlddoBEO6wDpx4Bq+IwnxYg7otYy61hQVjAbGYW2fksFhfzWFjIk3mhhGSm0J7gY3Lgs0Cg5ff74Xa5iJ7TU+AoOBWLVGQGb/b2Ud6sYKvyCsX1IlaWczRqEZvlHSIsVhBJrLZ5u+se+KivV0G7a/E8DxcF6lgdBnuHYDN6UCps4eTkGO9PT7G7u4ON9QLWVnMoFUuoVrfI3NxzBKdT7VBsQ+5wanKyC+Q9HsI73BgbfYrZRBbV1yIOxH0cHR5QpzWsFwrIrSwjk8lQ0DSJRiMIhxNtu/OfDqf8fgV1dunxeDoObzQsJ7GDQ5L+iVYyGEySXq+XTEajZDabJYvFIo1brZJWq5U4jrvRaDQYHmavlUr2Huiw2TrA6z+n3PlD0dPTA6VKhd5epruzLAsKhsFgAIXCZDLBaDRibGwM/f39v+g7j++AbqdLQeN+5L38dwq+UKvVTQrsimGYZl9fX5N+2PR5fU27zdZ0u93N8fHxpsPhuKDXbwMDA587R9GFTUxMKB58OpFpVIa6Y+x2O0OdMfSxTDQyQ+MytDeG9tfdaX93939ZvwFbBDoC8QrUigAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "hint text",
            "title": "hint text",
            "src": "/static/433b09fbcf0ca5b83879e79a195f7b56/3cbba/voiceover.png",
            "srcSet": ["/static/433b09fbcf0ca5b83879e79a195f7b56/7fc1e/voiceover.png 288w", "/static/433b09fbcf0ca5b83879e79a195f7b56/a5df1/voiceover.png 576w", "/static/433b09fbcf0ca5b83879e79a195f7b56/3cbba/voiceover.png 1152w", "/static/433b09fbcf0ca5b83879e79a195f7b56/392b1/voiceover.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
        <Caption mdxType="Caption">Screenshot of a rotor in action</Caption>
      </Column>
    </Row>
    <h2>{`Related`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/UI-shell-header/code/"
        }}>{`UI shell header`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/UI-shell-left-panel/code/"
        }}>{`UI shell left panel`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/UI-shell-right-panel/code/"
        }}>{`UI shell right panel`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.carbondesignsystem.com/components/breadcrumb/code/"
        }}>{`Breadcrumb`}</a></li>
    </ul>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`David R. Danielson, `}<a parentName="li" {...{
          "href": "https://www.researchgate.net/publication/240859594_Transitional_volatility_in_web_navigation"
        }}><em parentName="a">{`Transitional Volatility in Web Navigation`}</em></a>{` (2003)`}</li>
      <li parentName="ul">{`Susan Farrell, `}<a parentName="li" {...{
          "href": "https://www.nngroup.com/articles/utility-navigation/"
        }}><em parentName="a">{`Utility Navigation: What It Is and How to Design It`}</em></a>{` (2015)`}</li>
      <li parentName="ul">{`IBM Design, `}<a parentName="li" {...{
          "href": "http://accessibility-handbook.mybluemix.net/design/a11y-handbook/"
        }}><em parentName="a">{`Accessibility Handbook`}</em></a>{` (2019)`}</li>
      <li parentName="ul">{`James Kalbach, `}<a parentName="li" {...{
          "href": "https://www.oreilly.com/library/view/designing-web-navigation/9780596528102/ch04.html"
        }}><em parentName="a">{`Designing Web Navigation`}</em></a>{` (2007)`}</li>
      <li parentName="ul">{`WebAIM, `}<a parentName="li" {...{
          "href": "https://webaim.org/techniques/skipnav/"
        }}><em parentName="a">{`“Skip Navigation” Links`}</em></a>{` (2013)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/standards-guidelines/wcag/"
        }}>{`Web Content Accessibility Guidelines`}</a>{` (W3C, 2018)`}</li>
    </ul>
    <h2>{`Feedback`}</h2>
    <p>{`Help us improve this pattern by providing feedback, asking questions, and leaving any other comments on `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-website/issues/new?assignees=&labels=feedback&template=feedback.md"
      }}>{`GitHub`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      